import { useCallback, useEffect, useRef, useState } from 'react'

import { profileSocialApi } from '@/features/profile-social/api'

import type { DiscoveryFilters } from '../filters/filters'
import {
  captureAmbientLocation,
  claimAutoLocationAttempt,
  isAmbientLocationFresh,
  normalizeCityName,
  readAmbientLocation,
  writeAmbientLocation,
  type AmbientLocation,
} from '../location'
import { GeolocationError } from '../geo'

export interface UseDiscoveryLocationArgs {
  userId: string | null
  enabled: boolean
  filters: DiscoveryFilters
}

export interface DiscoveryLocationState {
  /** Latest per-user ambient fix, including a stale one used for the first query. */
  ambientLocation: AmbientLocation | null
  /** Best-effort `/me` city used when no explicit or ambient city is known. */
  profileCity: string | null
  /** Explicit/manual detection; errors stay visible in the city picker. */
  detectLocation: () => Promise<AmbientLocation>
}

interface AmbientState {
  userId: string | null
  location: AmbientLocation | null
}

interface ProfileState {
  userId: string | null
  city: string | null
}

/**
 * Discovery location lifecycle:
 *
 * - reads the per-user ambient fix synchronously so the first activity query is
 *   never blocked by geolocation;
 * - reuses a stale fix for the first query and refreshes GPS in the background;
 * - auto-requests at most once per five minutes per session (manual detection
 *   is always allowed);
 * - keeps profile-city persistence best-effort and independent from the list.
 */
export function useDiscoveryLocation({
  userId,
  enabled,
  filters,
}: UseDiscoveryLocationArgs): DiscoveryLocationState {
  const [ambientState, setAmbientState] = useState<AmbientState>(() => ({
    userId,
    location: userId ? readAmbientLocation(userId) : null,
  }))
  // Derive on user change instead of synchronously setting state in an effect.
  // The previous account's fix is never exposed.
  const ambientLocation =
    ambientState.userId === userId
      ? ambientState.location
      : userId
        ? readAmbientLocation(userId)
        : null

  const [profileState, setProfileState] = useState<ProfileState>({
    userId,
    city: null,
  })
  const profileCity =
    profileState.userId === userId ? profileState.city : null

  // Latest filter key without re-running a background GPS request whenever a
  // filter changes. A result is applied only if the filters are still current.
  const filtersKey = JSON.stringify(filters)
  const filtersKeyRef = useRef(filtersKey)
  useEffect(() => {
    filtersKeyRef.current = filtersKey
  }, [filtersKey])

  const userIdRef = useRef(userId)
  useEffect(() => {
    userIdRef.current = userId
  }, [userId])

  const profileCityRef = useRef<{ userId: string | null; city: string | null }>({
    userId,
    city: profileCity,
  })
  useEffect(() => {
    profileCityRef.current = { userId, city: profileCity }
  }, [profileCity, userId])

  const profileTouchedRef = useRef<{
    userId: string | null
    touched: boolean
  }>({ userId: null, touched: false })
  const requestIdRef = useRef(0)

  // Seed the header from `/me` once per user/mount. It is only a fallback: a
  // detected city or a manual selection always takes precedence.
  useEffect(() => {
    if (!userId) return
    const controller = new AbortController()
    void profileSocialApi
      .getMe({ signal: controller.signal })
      .then((me) => {
        if (controller.signal.aborted) return
        if (
          profileTouchedRef.current.userId === userId &&
          profileTouchedRef.current.touched
        ) {
          return
        }
        const city = normalizeCityName(me.city)
        profileCityRef.current = { userId, city }
        setProfileState({ userId, city })
      })
      .catch(() => {
        // The profile city is optional; discovery still works without it.
      })
    return () => controller.abort()
  }, [userId])

  const syncProfileCity = useCallback(
    (detected: string | null) => {
      if (!userId) return
      const normalized = normalizeCityName(detected)
      if (!normalized) return
      const current =
        profileCityRef.current.userId === userId
          ? profileCityRef.current.city
          : null
      if (current && current.toLowerCase() === normalized.toLowerCase()) {
        return
      }
      // Mark the city as locally authoritative before patching so a late
      // `/me` response cannot overwrite the detected header value.
      profileTouchedRef.current = { userId, touched: true }
      void profileSocialApi
        .updateMe({ city: normalized })
        .then((subset) => {
          if (userIdRef.current !== userId) return
          const city = normalizeCityName(subset.city) ?? normalized
          profileCityRef.current = { userId, city }
          setProfileState({ userId, city })
        })
        .catch(() => {
          // Profile persistence is best-effort and never blocks detection.
        })
    },
    [userId],
  )

  const detectLocation = useCallback(async (): Promise<AmbientLocation> => {
    if (!userId) throw new GeolocationError('unsupported')
    requestIdRef.current += 1
    const next = await captureAmbientLocation()
    if (userIdRef.current !== userId) {
      throw new GeolocationError('unsupported')
    }
    writeAmbientLocation(userId, next)
    setAmbientState({ userId, location: next })
    syncProfileCity(next.city)
    return next
  }, [syncProfileCity, userId])

  useEffect(() => {
    if (!enabled || !userId) return
    if (ambientLocation && isAmbientLocationFresh(ambientLocation)) return
    // The session guard records the attempt even when GPS fails, so a denied
    // permission does not re-prompt on every tab switch.
    if (!claimAutoLocationAttempt(userId)) return

    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId
    const filtersSnapshot = filtersKeyRef.current
    let cancelled = false

    void (async () => {
      try {
        const next = await captureAmbientLocation()
        if (cancelled) return
        if (userIdRef.current !== userId) return
        if (requestIdRef.current !== requestId) return
        // The user changed filters while GPS was resolving: keep the current
        // list instead of replacing it with a query built from another state.
        if (filtersKeyRef.current !== filtersSnapshot) return
        writeAmbientLocation(userId, next)
        setAmbientState({ userId, location: next })
        syncProfileCity(next.city)
      } catch {
        // Automatic detection is intentionally silent; the manual picker is
        // where geolocation errors are shown.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [ambientLocation, enabled, syncProfileCity, userId])

  return { ambientLocation, profileCity, detectLocation }
}
